import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { IEvent } from '@app/api/interfaces/event.interface';
import {
  DEFAULT_ICON_BADGE_BACKGROUND,
  IconBadgeComponent,
} from '@app/shared/components/icon-badge/icon-badge.component';
import { ldEventStatusLocalize } from '@app/main/leak-detection/localize/ld-event-status';
import { HIDDEN_EVENT_BADGE_BACKGROUND } from '@app/main/leak-detection/configs/ld-event-status.config';
import { getLdEventStatusBackground, getLdEventStatusIcon } from '@app/main/leak-detection/utils/ld-event-status-icon';
import { deriveLdEventVisibility } from '@app/main/leak-detection/utils/ld-event-status';
import { LdEventVisibility } from '@app/main/leak-detection/enums/ld-event-visibility.enum';

/**
 * An event's status as a badge — the single source of this look, used by the Events table and,
 * through `iconComponent`, by the leak-detection context menus.
 *
 * Takes the whole event rather than a resolved status so it owns the derivation: which glyph the
 * status maps to, and which fill sits behind it (a hidden event drops its colour for grey).
 * `gc-icon-badge` stays an implementation detail in here.
 *
 * Pass `iconSrc` to show another of the event's icons — an investigation result, say — on the same
 * badge. Those get the neutral fill, because the status colours describe the status dimension only.
 */
@Component({
  selector: 'gc-ld-event-status-icon',
  standalone: true,
  imports: [TranslatePipe, IconBadgeComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './ld-event-status-icon.component.html',
  host: {
    class: 'flex shrink-0',
  },
})
export class LdEventStatusIconComponent {
  public event = input.required<IEvent>();

  /** Glyph override; defaults to the event's status glyph. */
  public iconSrc = input<string | undefined>(undefined);

  public size = input<`${number}rem`>('1.625rem');

  /** overrides the badge defaults — prefix a class with `!` to win over them, e.g. `!border-[1.5px]` */
  public styleClass = input<string>('');

  private readonly status = computed(() => this.event().status);

  protected readonly icon = computed(() => this.iconSrc() ?? getLdEventStatusIcon(this.status()));

  protected readonly label = computed(() => ldEventStatusLocalize[this.status()]);

  protected readonly background = computed(() => {
    if (deriveLdEventVisibility(this.event()) === LdEventVisibility.Hidden) return HIDDEN_EVENT_BADGE_BACKGROUND;

    return this.iconSrc() ? DEFAULT_ICON_BADGE_BACKGROUND : getLdEventStatusBackground(this.status());
  });
}
